import { useState } from 'react';

import Card from '@/components/Card';
import Stack from '@/components/Stack';
import { Body, Small } from '@/components/Typography';
import { WorkerComment, WorkersV2API } from '@/services/workersV2API';
import AddCommentModal from './AddCommentModal';

type Props = {
  workerId: number;
  comments: WorkerComment[];
  onCommentAdded: () => void;
};

export const COMMENT_BODY_STYLE = {
  overflowWrap: 'anywhere',
  maxWidth: '100%',
  minWidth: 0,
} as const;

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
};

const CommentsCard = ({ workerId, comments, onCommentAdded }: Props) => {
  const [showAddModal, setShowAddModal] = useState(false);

  const handleAddComment = async (body: string) => {
    const response = await WorkersV2API.addComment(workerId, body);
    if (!response.success) {
      throw new Error('Failed to add comment');
    }
    onCommentAdded();
  };

  return (
    <>
      {showAddModal && (
        <AddCommentModal
          onClose={() => setShowAddModal(false)}
          onSubmit={handleAddComment}
        />
      )}

      <Card
        actions={[
          {
            label: 'Add',
            onAction: () => setShowAddModal(true),
          },
        ]}
        title="Comments"
      >
        <Card.Section>
          {!comments || comments.length === 0 ? (
            <Stack style={{ justifyContent: 'center', padding: '20px' }}>
              <Small color="lighter">No comments yet</Small>
            </Stack>
          ) : (
            <Stack vertical gap={16}>
              {comments.map((comment) => (
                // BH-81: a column flex item is as wide as its longest word, so
                // one 400-letter word made a long comment ~3,700px wide, over
                // the Worker Details column. It now breaks inside the card.
                <Stack
                  key={comment.id}
                  vertical
                  gap={4}
                  style={{ minWidth: 0 }}
                >
                  {/* On a wrapper: Body does not pass a style prop through. */}
                  <div
                    data-testid={`commentBody-${comment.id}`}
                    style={COMMENT_BODY_STYLE}
                  >
                    <Body>{comment.body}</Body>
                  </div>
                  <Small color="theme">
                    {formatDate(comment.createdAt)}
                    {comment.actor && (
                      <>
                        {' '}
                        &mdash; {comment.actor.firstName}{' '}
                        {comment.actor.lastName}
                      </>
                    )}
                  </Small>
                </Stack>
              ))}
            </Stack>
          )}
        </Card.Section>
      </Card>
    </>
  );
};

export default CommentsCard;
